import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { isNativeMobile } from '../../config/api';

/** Cold-start launch URL must be consumed once — getLaunchUrl() keeps returning it for the whole session. */
let launchUrlHandled = false;

/**
 * Handles deep links (Universal Links on iOS, App Links on Android) so that
 * when the user taps "View Details" in an email notification, the app opens
 * and navigates to the correct page instead of the browser.
 *
 * Listens for appUrlOpen (app already running) and getLaunchUrl() (cold start).
 */
export function AppUrlListener() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isNativeMobile()) return;

    const navigateToUrl = (url: string, replace = false) => {
      try {
        const parsed = new URL(url);
        const path = parsed.pathname + parsed.search;
        if (path && path !== '/') {
          navigateRef.current(path, { replace });
        }
      } catch {
        // Ignore invalid URLs
      }
    };

    const handleUrl = (event: URLOpenListenerEvent) => {
      if (event.url) navigateToUrl(event.url);
    };

    const listener = App.addListener('appUrlOpen', handleUrl);

    if (!launchUrlHandled) {
      App.getLaunchUrl()
        .then((result) => {
          if (launchUrlHandled) return;
          if (result?.url) {
            launchUrlHandled = true;
            navigateToUrl(result.url, true);
          }
        })
        .catch(() => {});
    }

    return () => {
      listener.then((l) => l.remove());
    };
  }, []);

  return null;
}
